import { useEffect, useState } from 'react';
import { Box, Button, Divider, Stack } from '@mui/material';
import type { FilterBarFilterOption, FilterValue } from '../contracts';
import type { FilterCondition } from 'framework/types';
import FilterOptionListbox from './FilterOptionListbox';

export interface FilterBarDropdownFilterConfig {
  type: 'dropdown';
  id: string;
  source: string;
  condition: FilterCondition;
  label: string;
  options: FilterBarFilterOption[];
  multiple?: boolean;
}

interface DropdownFilterEditorProps {
  filter: FilterBarDropdownFilterConfig;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  onClose: () => void;
}

const getValues = (value: FilterValue) => (Array.isArray(value) ? value : value ? [value] : []);

const DropdownFilterEditor = ({ filter, value, onChange, onClose }: DropdownFilterEditorProps) => {
  const [draftValues, setDraftValues] = useState(() => getValues(value));

  useEffect(() => setDraftValues(getValues(value)), [filter.id, value]);

  if (!filter.multiple) {
    const selectedValue = getValues(value)[0];

    return (
      <Box sx={{ minWidth: 220 }}>
        <FilterOptionListbox
          label={`${filter.label} options`}
          options={filter.options}
          selectedValues={selectedValue ? [selectedValue] : []}
          onSelect={optionValue => {
            onChange(optionValue);
            onClose();
          }}
        />
      </Box>
    );
  }

  const toggleOption = (optionValue: string) => {
    setDraftValues(current =>
      current.includes(optionValue)
        ? current.filter(currentValue => currentValue !== optionValue)
        : [...current, optionValue],
    );
  };

  return (
    <Box sx={{ width: 390 }}>
      <FilterOptionListbox
        label={`${filter.label} options`}
        options={filter.options}
        selectedValues={draftValues}
        multiple
        onSelect={toggleOption}
      />
      <Divider />
      <Stack direction="row" spacing={2} sx={{ p: 2 }}>
        <Button
          variant="outlined"
          onClick={() => {
            onChange(draftValues);
            onClose();
          }}
        >
          Update
        </Button>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
      </Stack>
    </Box>
  );
};

export default DropdownFilterEditor;
